import React from 'react';
import * as LucideIcons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../data/mockData';

const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1516549655169-df83a0774514"
            alt="Veteriner hizmetleri"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Hizmetlerimiz
          </h1>
          <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
            Evcil dostlarınızın sağlığı için kapsamlı ve profesyonel veteriner hizmetleri
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/90 backdrop-blur-sm p-3 rounded-xl inline-flex">
                        {IconComponent && <IconComponent className="h-7 w-7 text-emerald-700" />}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-emerald-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-emerald-900 mb-12 text-center">
            Detaylı Hizmet Bilgileri
          </h2>

          <div className="space-y-16">
            {/* Surgery */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h3 className="text-3xl font-bold text-emerald-900 mb-4">
                  Cerrahi İşlemler
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Deneyimli cerrahlarımız, modern ameliyathane ekipmanlarımızla güvenli ve başarılı 
                  ameliyatlar gerçekleştirir. Kısırlaştırma, kastrasyondan karmaşık cerrahi müdahalelere 
                  kadar geniş bir yelpazede hizmet sunuyoruz.
                </p>
                <ul className="space-y-3">
                  {[
                    'Yumuşak doku cerrahisi',
                    'Ortopedik cerrahi',
                    'Kısırlaştırma ve kastrasyon',
                    'Tümör cerrahisi',
                    'Acil cerrahi müdahaleler'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-gray-700">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src="https://images.unsplash.com/photo-1770836037326-d2df574278b3"
                  alt="Cerrahi"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </div>

            {/* Internal Medicine */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1631815587646-b85a1bb027e1"
                  alt="İç hastalıklar"
                  className="rounded-2xl shadow-xl"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-emerald-900 mb-4">
                  İç Hastalıklar
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Evcil hayvanlarınızın iç hastalıklarının teşhis ve tedavisinde uzman ekibimiz, 
                  kapsamlı muayene ve ileri tanı yöntemleriyle en doğru tedaviyi sağlar.
                </p>
                <ul className="space-y-3">
                  {[
                    'Sindirim sistemi hastalıkları',
                    'Kalp ve solunum problemleri',
                    'Böbrek ve karaciğer hastalıkları',
                    'Hormonal bozukluklar',
                    'İdrar yolu enfeksiyonları'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-gray-700">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Dermatology */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h3 className="text-3xl font-bold text-emerald-900 mb-4">
                  Dermatoloji
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Cilt hastalıkları, alerjiler ve deri problemlerinde özel tedavi uyguluyoruz. 
                  Dermatoloji uzmanlarımız, evcil dostlarınızın cilt sağlığını korumak için özenle çalışır.
                </p>
                <ul className="space-y-3">
                  {[
                    'Alerji testleri ve tedavisi',
                    'Mantar ve parazit tedavisi',
                    'Cilt enfeksiyonları',
                    'Tüy dökülmesi tedavisi',
                    'Kaşıntı ve egzama tedavisi'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-gray-700">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src="https://images.unsplash.com/photo-1638202993928-7267aad84c31"
                  alt="Dermatoloji"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-800 via-emerald-700 to-amber-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Hizmetlerimiz Hakkında Daha Fazla Bilgi Almak İster Misiniz?
          </h2>
          <p className="text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
            Uzman veterinerlerimizle iletişime geçin ve evcil dostlarınız için en uygun tedavi planını belirleyin
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/randevu"
              className="bg-white text-emerald-800 px-8 py-4 rounded-lg font-semibold inline-flex items-center gap-2 hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl"
            >
              Randevu Al
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/iletisim"
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
            >
              Bize Ulaşın
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
